import { useRef, useMemo } from 'react';
import {
  useField,
  useDynamicList,
  useForm,
  SubmitResult,
  notEmptyString,
} from '@shopify/react-form';
import useSlingApi from '@sdk/api/useSlingApi';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import gettext from '@sdk/lang/gettext';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';

import { FormSubmitStatuses } from '@web/forms/types';
import {
  FieldValues,
  UseCreateTimesheetFormProps,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import {
  getLocationOptions,
  getPositionOptions,
  getFieldValues,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';
import {
  getEmptyTimeClockEntry,
  markInvalidTimeClockEntries,
} from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';
import {
  singleChoiceValidator,
  isValidTimeValue,
  breaksValidator,
  clockOutValidator,
  validateCreateTimesheetForm,
} from '@web/timesheets/CreateTimesheetModal/utils/validation';
import { mapTimesheetFormFieldsToBulkPayload } from '@web/timesheets/CreateTimesheetModal/utils/mappers';
import useRequireAssigneeConsent from '@web/timesheets/EditTimesheetModal/hooks/useRequireAssigneeConsent';

/**
 * This method creates Time Clock entries section - dynamic list of entries, that are clock in and clock out times.
 */
export const useTimeClockSection = () => {
  const emptyEntryRef = useRef(getEmptyTimeClockEntry());

  return useDynamicList(
    {
      list: [emptyEntryRef.current],
      validates: {
        clockIn: [notEmptyString(gettext('Clock in time is missing')), isValidTimeValue],
        clockOut: clockOutValidator,
        breaks: breaksValidator,
      },
    },
    getEmptyTimeClockEntry
  );
};

export const useCreateTimesheetModalForm = ({ initialDate }: UseCreateTimesheetFormProps) => {
  // @INFO a workaround to let ProgressButton work with form submit handler
  const submitPromise = useRef<Promise<SubmitResult>>();
  const personaCollection = usePersonaCollection();
  const api = useSlingApi();
  const locationOptions = useMemo(
    () => getLocationOptions(personaCollection.locations),
    [personaCollection.locations]
  );
  const positionOptions = useMemo(
    () => getPositionOptions(personaCollection.positions),
    [personaCollection.positions]
  );
  const requireAssigneeConsent = useRequireAssigneeConsent();

  const employeeField = useField<PersonaPickerOption[]>({
    value: [],
    validates: singleChoiceValidator,
  });
  const locationField = useField<PersonaPickerOption[]>({
    value: [],
    validates: singleChoiceValidator,
  });
  const positionField = useField<PersonaPickerOption[]>({
    value: [],
    validates: singleChoiceValidator,
  });
  const dateField = useField<Date>(initialDate);
  const timeClockSection = useTimeClockSection();

  const formData = useForm({
    fields: {
      employee: employeeField,
      location: locationField,
      position: positionField,
      date: dateField,
    },
    dynamicLists: {
      timeClock: timeClockSection,
    },
    onSubmit: async (fieldValues: FieldValues) => {
      const formErrors = validateCreateTimesheetForm(fieldValues);

      if (Object.keys(formErrors).length) {
        markInvalidTimeClockEntries({ formErrors, timeClockFields: timeClockSection.fields });

        submitPromise.current = Promise.reject({
          status: FormSubmitStatuses.fail,
          errors: {
            formErrors,
          },
        });
        return submitPromise.current;
      }

      const payload = mapTimesheetFormFieldsToBulkPayload(fieldValues).filter(
        // This is dedicated method for creation (not edit) of timesheet, therefore we can skip all the deleted records.
        ({ deleted }) => !deleted
      );

      try {
        await api.timesheets.bulkUpdate(payload, requireAssigneeConsent);
        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        return submitPromise.current;
      } catch (error) {
        submitPromise.current = Promise.reject({
          status: FormSubmitStatuses.fail,
          errors: getErrorForToast(error),
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formData.submit, submitPromise);

  /**
   * Extracted form values, without field methods.
   * Including dynamicList data (timeClock section).
   */
  const formValues = useMemo(() => getFieldValues(formData), [formData]);

  return {
    formData,
    formValues,
    submit,
    locationOptions,
    positionOptions,
  };
};
